/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import { Button, CircularProgress, TextField, withStyles } from '@material-ui/core'
import { css, jsx } from '@emotion/core'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { theme } from '../../../constants'
import OrganizationService from '../../../services/organization'
import Multiselect from '../../components/Multiselect'
import * as Actions from '../../admin/actions'

const styles = {}

styles.blockingOverlay = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999;
  background: rgba(0, 0, 0, 0.5);
`

styles.box = css`
  position: absolute;
  width: 700px;
  border-radius: 5px;
  padding: 30px 60px;
  background: white;
  border: 10px solid lightgray;
  pointer-events: all;
`

styles.title = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: bold;
  color: ${theme.palette.common.black};
`

styles.error = css`
  color: ${theme.palette.error.main};
  margin: 0px 30px 15px 30px;
`

styles.emailsContainer = css`
  width: 100%;
  margin: 18px 0px;
  display: flex;
  flex-direction: column;
  color: ${theme.palette.common.black};
`

styles.descriptionContainer = css`
  width: 100%;
  margin: 14px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  color: ${theme.palette.common.black};
`

styles.label = css`
  width: 30%;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
`

styles.description = css`
  width: 70%;
  display: flex;
  align-items: center;
  font-size: 14px;
`

styles.selectForm = css`
  width: 100%;
  height: 40px;
  font-weight: 500;
  background: transparent;

  &:focus {
    outline: 2px solid #4a90e2; /* example color */
    outline-offset: 2px; /* optional: separates the outline from the element */
  }
`

styles.actionContainer = css`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-top: 10px;
`

styles.actionButton = css`
  height: 24px;
  padding: 0px 20px;
  text-transform: none;
  font-size: 14px;
  background: ${theme.palette.common.blue};
  color: ${theme.palette.common.white};

  &:hover {
    background: ${theme.palette.common.blue2};
  }
`

styles.cancelButton = css`
  height: 24px;
  padding: 0px 20px;
  margin-right: 15px;
  text-transform: none;
  font-size: 14px;
  background: transparent;
  color: ${theme.palette.common.blue};
  box-shadow: none;

  &:hover {
    background: transparent;
  }
`

styles.loaderContainer = css`
  display: flex;
  color: white;
  margin-right: 10px;
`

styles.error = css`
  color: ${theme.palette.error.main};
  height: 20px;
  text-align: right;
  font-size: 14px;
`

styles.iconClose = css`
  position: absolute;
  display: flex;
  color: grey;
  top: 20px;
  right: 24px;
  align-items: flex-start;
  justify-content: flex-end;
  font-size: 28px;
  cursor: pointer;
`

const CssTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      color: theme.palette.common.black
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.palette.text.disabled}`
    },
    '& .Mui-disabled': {
      color: `${theme.palette.text.disabled} !important`
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '12px',
      color: theme.palette.common.black
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: `${theme.palette.text.disabled} !important`,
        borderRadius: '8px'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 14px',
      color: theme.palette.common.black
    }
  }
})(TextField)

const CssMultiselect = withStyles({
  root: {
    width: '100%',

    '& .MuiFormLabel-root': {
      color: theme.palette.common.black,
      fontSize: '19px',
      fontWeight: 'bold'
    },
    '& .MuiInput-formControl': {
      marginTop: '24px'
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.palette.text.disabled}`
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.text.disabled
      }
    },
    '& .MuiChip-root': {
      color: theme.palette.common.white,
      background: theme.palette.common.blue
    },
    '& .MuiChip-deleteIcon': {
      color: 'rgba(255, 255, 255, 0.75)',

      '&:hover': {
        color: theme.palette.common.white
      }
    }
  }
})(Multiselect)

const CssButton = withStyles({
  root: {
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5)'
    }
  }
})(Button)

const inputText = {
  input: { color: theme.palette.common.black },
  label: { color: theme.palette.common.black },
  width: '100%',
  marginTop: '8px',
  marginBottom: 0
}

function AddOrEditOrganizationModal(props) {
  const [name, setName] = React.useState('')
  const [users, setUsers] = React.useState([])
  const [allUsers, setAllUsers] = React.useState([])
  const [suggestions, setSuggestions] = React.useState([])
  const [isEnabled, setIsEnabled] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [showError, setShowError] = React.useState('')

  React.useEffect(() => {
    const allOrganizationUsers = props.ownerOrganization.users.map(v => {
      if (v.user) {
        return { value: v.user.email, label: `${v.user.name} ${v.user.last_name}` }
      }

      return { value: v.email, label: v.email }
    })
    setAllUsers(allOrganizationUsers)

    if (props.organization) {
      setName(props.organization.name)

      const organizationUsers = props.ownerOrganization.users
        .filter(
          v =>
            props.organization &&
            v.organizations.some(w => w.organization._id === props.organization._id)
        )
        .map(v => ({ value: v.email, label: v.email }))
      setUsers(organizationUsers)

      const suggestionUsers = allOrganizationUsers.filter(
        v => !organizationUsers.some(w => v.value === w.value)
      )
      setSuggestions(suggestionUsers)
    } else {
      setSuggestions(allOrganizationUsers)
    }
  }, [props.organization, props.ownerOrganization])

  // Allow saving only if something has changed.
  function updateIsEnabled(organizationName, organizationUsers) {
    const isValidName = !!organizationName
    const nameHasChanged = !props.organization || props.organization.name !== organizationName

    const savedOrganizationUsers = props.ownerOrganization.users
      .filter(
        v =>
          props.organization &&
          v.organizations.some(w => w.organization._id === props.organization._id)
      )
      .map(v => v.email)
    const usersHaveChanged =
      !props.organization ||
      savedOrganizationUsers.length !== organizationUsers.length ||
      savedOrganizationUsers.some(v => !organizationUsers.some(w => w.value === v))

    if (!isValidName) {
      setShowError(window.I18n.t('groups.enterValidName'))
      setIsEnabled(false)
    } else if (!nameHasChanged && !usersHaveChanged) {
      setShowError('')
      setIsEnabled(false)
    } else {
      setShowError('')
      setIsEnabled(true)
    }
  }

  function onNameChange(event) {
    const organizationName = event.target.value
    setName(organizationName)
    updateIsEnabled(organizationName, users)
  }

  function onChangeUsers(organizationUsers) {
    setUsers(organizationUsers)
    updateIsEnabled(name, organizationUsers)

    const suggestionUsers = allUsers.filter(v => !organizationUsers.some(w => v.value === w.value))
    setSuggestions(suggestionUsers)
  }

  async function onSubmit() {
    // On submit, show loader and disable submit to not perform many retries with the same key.
    setIsLoading(true)
    setIsEnabled(false)

    try {
      const payload = { name, userEmails: users.map(v => v.value) }

      if (!props.organization) {
        await OrganizationService.createOrganization(props.ownerOrganization._id, payload)
      } else {
        payload.id = props.organization._id
        await OrganizationService.updateOrganization(props.ownerOrganization._id, payload)
      }

      // Reload UI.
      await Actions.updateUser()
      onClose()
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message
      if (message) {
        setShowError(message)
        if (!showError) {
          setTimeout(() => setShowError(''), 5000)
        }

        setIsLoading(false)
      } else {
        console.error(error)
        toast.error(window.I18n.t('groups.addOrUpdateErrorMessage'))
        onClose()
      }
    }
  }

  function onClose() {
    props.onApply()
  }

  const selectedEmails = users.map(v => {
    const organizationUser = props.ownerOrganization.users.find(w => w.email === v.value)
    const user = organizationUser && organizationUser.user
    const label = user ? user.email : v.label

    return { ...v, label }
  })

  const suggestionsWithEmails = suggestions.map(v => {
    const organizationUser = props.ownerOrganization.users.find(w => w.email === v.value)
    const user = organizationUser && organizationUser.user
    const label = user ? `${user.name} ${user.last_name} (${user.email})` : v.label

    return { ...v, label }
  })

  return (
    <div css={styles.blockingOverlay}>
      <div css={styles.box}>
        <span css={styles.title}>
          {window.I18n.t(props.organization ? 'groups.editGroup' : 'groups.createGroup')}
        </span>
        <div css={styles.emailsContainer}>
          <span css={styles.label}>{window.I18n.t('groups.groupName')}</span>
          <CssTextField
            variant="outlined"
            placeholder={window.I18n.t('groups.enterGroupName')}
            value={name}
            onChange={onNameChange}
            type="name"
            name="name"
            margin="normal"
            css={inputText}
          />
        </div>

        <div css={styles.descriptionContainer}>
          <CssMultiselect
            name="users"
            label={window.I18n.t('groups.groupMembers')}
            placeholder={window.I18n.t('groups.enterEmails')}
            onChange={onChangeUsers}
            fetchSuggestions={false}
            suggestions={suggestionsWithEmails}
            initialSelected={selectedEmails}
          />
        </div>

        <div css={styles.error}>{showError}</div>
        <div css={styles.actionContainer}>
          <CssButton
            color="primary"
            variant="contained"
            css={styles.cancelButton}
            disabled={isLoading}
            onClick={onClose}
          >
            {window.I18n.t('common.cancel')}
          </CssButton>
          <CssButton css={styles.actionButton} disabled={!isEnabled} onClick={onSubmit}>
            {isLoading && (
              <div css={styles.loaderContainer}>
                <CircularProgress size={16} thickness={4} color="inherit" />
              </div>
            )}
            {window.I18n.t(props.organization ? 'common.save' : 'common.create')}
          </CssButton>
        </div>
        <span
          css={styles.iconClose}
          style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
          onClick={onClose}
        >
          {' '}
          x{' '}
        </span>
      </div>
    </div>
  )
}

AddOrEditOrganizationModal.defaultProps = {
  organization: null
}

AddOrEditOrganizationModal.propTypes = {
  user: PropTypes.object.isRequired,
  organization: PropTypes.object,
  ownerOrganization: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired
}

export default AddOrEditOrganizationModal
